// app/components/Header/Header.tsx
'use client'

import React, { useState, useEffect } from 'react'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import Image from 'next/image'
import { useRouter, useParams, usePathname } from 'next/navigation'
import ThemeToggle from '../ThemeToggle'
import { NewWidgetButton } from './components/NewWidgetButton'
import { SymbolSearchModal, SymbolButton } from '../SymbolSearch/SymbolSearch'
import { useGlobalAIState } from '../AITerminal/globalAIState'

function IconSeparator({ className }: { className?: string }) {
  return <span className={`mx-2 ${className}`}>|</span>
}

// export function UserOrLogin() {
//   return (
//     <>
//       <Link href="https://nin.in/trade/" rel="nofollow">
//         <Image
//           src={document.documentElement.classList.contains('light')
//             ? "/logo_trade_dark.svg"
//             : "/logo_trade_light_baseline.svg"}
//           alt="NINtrade Logo"
//           width={100}
//           height={30}
//           priority={true}
//         />
//       </Link>

//       <div className="flex items-center font-semibold">
//         <IconSeparator className="text-gray-500" />
//         <Link href="/">Terminal</Link>
//         <IconSeparator className="text-gray-500" />
//         <NewWidgetButton /> {/* Use Client Component here */}
//       </div>
//     </>
//   )
// }

// We've moved the SymbolSearchModal to a separate component file

// app/components/Header/Header.tsx


// Dynamically import AuthStatus (client-only, no SSR)
const AuthStatus = dynamic(
  () => import('../Auth/AuthStatus').then(mod => mod.AuthStatus),
  {
    ssr: false,
    loading: () => <div>Loading...</div>,
  }
)

// AI Terminal Button Component
const AITerminalButton = () => {
  const { isAIChatOpen, isAITerminalActive, toggleAIChat, isHydrated } = useGlobalAIState()
  
  // Don't render until hydrated to prevent layout shift
  if (!isHydrated) {
    return null
  }
  
  // Only render if AITerminal is active
  if (!isAITerminalActive) {
    return null
  }
  
  return (
    <button
      onClick={toggleAIChat}
      className={`p-2 rounded-lg transition-colors ${
        isAIChatOpen 
          ? 'bg-[#21728D] hover:bg-[#21728D77]' 
          : 'bg-gray-200 dark:bg-gray-800 hover:bg-gray-300 dark:hover:bg-gray-700'
      }`}
      aria-label="Toggle AI Assistant"
      title="AI Assistant"
      data-tutorial="ai-terminal-button"
    >
      <div className={`h-5 w-5 flex items-center justify-center text-sm ${
        isAIChatOpen ? 'text-white' : 'text-gray-800 dark:text-gray-100'
      }`}>
        🤖
      </div>
    </button>
  )
}

export function Header() {
  const params = useParams()
  const router = useRouter()
  const pathname = usePathname()
  const symbol = (params?.symbol as string) || 'SYMBOL'

  // Check if we're on X pages
  const isXPage = pathname === '/x' || pathname?.startsWith('/x/')

  // show/hide the symbol picker
  const [showModal, setShowModal] = useState(false)

  function handleSymbolSelect(newSymbol: string) {
    // save to recents
    const stored = localStorage.getItem('recentSymbols') || '[]'
    const recents = JSON.parse(stored) as string[]
    const updated = [newSymbol, ...recents.filter((s) => s !== newSymbol)].slice(0, 5)
    localStorage.setItem('recentSymbols', JSON.stringify(updated))

    setShowModal(false)
    window.location.href = `/${newSymbol.toUpperCase()}`
  }

  // logo light/dark swap
  const [logoSrc, setLogoSrc] = useState('/logo_trade_dark.svg')
  useEffect(() => {
    const onTheme = (e: CustomEvent<{ isDark: boolean }>) =>
      setLogoSrc(
        e.detail.isDark
          ? '/logo_trade_light_baseline.svg'
          : '/logo_trade_dark.svg'
      )
    window.addEventListener('themeChange', onTheme as any)
    return () => window.removeEventListener('themeChange', onTheme as any)
  }, [])

  return (
    <>
      <header className="relative sticky top-0 z-50 flex items-center justify-between w-full h-16 px-4 border-b border-gray-600 bg-gradient-to-b from-black-900 via-black-800 to-gray-700 backdrop-blur-xl">
        {/* LEFT: logo + nav */}
        <div className="flex items-center">
          <React.Suspense fallback={<div>Loading...</div>}>
            <div className="relative">
              <Link href="https://nin.in/trade/" rel="nofollow">
                <Image
                  src={logoSrc}
                  alt="NINtrade Logo"
                  width={60}
                  height={30}
                  priority
                />
              </Link>
              {/* Beta tag - easy to remove */}
              <span 
                className="absolute -top-2 -right-4 px-1.5 py-0.5 text-xs font-medium text-[#CF7381] rounded-[10px]"
                style={{ backgroundColor: '#88a1ac33' }}
              >
                Beta
              </span>
            </div>
            <div className="flex items-center font-semibold ml-4 mt-1">
              <IconSeparator className="text-gray-500" />
              <Link href="/">{isXPage ? 'X' : 'Terminal'}</Link>
              {!isXPage && (
                <>
                  <IconSeparator className="text-gray-500" />
                  <NewWidgetButton />
                </>
              )}
            </div>
          </React.Suspense>
        </div>

        {/* CENTER: symbol chip - hide on X pages */}
        {!isXPage && (
          <SymbolButton
            symbol={symbol}
            onClick={() => setShowModal(true)}
            className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2"
          />
        )}

        {/* RIGHT: auth + theme */}
        <div className="flex items-center gap-4">
          <React.Suspense fallback={<div>Loading...</div>}>
           <div className="hidden sm:flex items-center auth-status-container h-9">
              {/* <Link href="/help" className="text-sm font-medium hover:text-primary mr-4">
                Help
              </Link> */}
              <AuthStatus />
            </div>
          </React.Suspense>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            {!isXPage && <AITerminalButton />}
          </div>
        </div>
      </header>

      {/* Modal Popup */}
      {showModal && (
        <SymbolSearchModal
          onSelect={handleSymbolSelect}
          onClose={() => setShowModal(false)}
          title="Select Symbol"
        />
      )}
    </>
  )
}
